import { Box, Typography } from '@mui/material'
import type * as React from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import type {
  RubyToken,
  RealtimeDiagramData,
  RealtimeDiagramLineText,
  RealtimeDiagramMultiLineText,
} from '../interfaces/ConversationIf'
import { ConversationImageDelButton } from './ConversationImageDelButton'

type RealtimeDiagramPreviewProps = {
  diagram: RealtimeDiagramData
  editMode?: boolean
  visualId?: string
  onDeleteRealtimeDiagram?: (visualId: string) => void
  scale?: number
}

const ITEM_COLORS = ['#F5B400', '#009FD3', '#F06A00', '#6F3CC3']

const BASE_WIDTH = 200

const normalizeIconName = (iconName?: string): string | null => {
  if (!iconName) {
    return null
  }

  const value = iconName.trim().toLowerCase()

  if (!/^[a-z0-9_]+$/.test(value)) {
    return null
  }

  return value
}

const MATERIAL_ICON_RENDERABLE_CACHE = new Map<string, Promise<boolean>>()

const isRenderableMaterialIcon = (iconName: string): Promise<boolean> => {
  const cached = MATERIAL_ICON_RENDERABLE_CACHE.get(iconName)
  if (cached) {
    return cached
  }

  const promise = new Promise<boolean>(resolve => {
    if (typeof document === 'undefined' || !document.body) {
      resolve(false)
      return
    }

    const fontSize = 29

    const test = document.createElement('span')
    test.className = 'material-symbols-outlined'
    test.textContent = iconName

    test.style.position = 'absolute'
    test.style.left = '-9999px'
    test.style.top = '-9999px'
    test.style.visibility = 'hidden'
    test.style.whiteSpace = 'nowrap'
    test.style.fontSize = `${fontSize}px`
    test.style.lineHeight = '1'
    test.style.fontVariationSettings =
      "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24"

    document.body.appendChild(test)

    const check = () => {
      window.requestAnimationFrame(() => {
        const rect = test.getBoundingClientRect()

        if (test.parentNode) {
          test.parentNode.removeChild(test)
        }

        const renderable =
          rect.width > 0 &&
          rect.height > 0 &&
          rect.width <= fontSize * 1.35 &&
          rect.height <= fontSize * 1.35

        resolve(renderable)
      })
    }

    const documentWithFonts = document as Document & {
      fonts?: {
        ready?: Promise<unknown>
      }
    }

    if (documentWithFonts.fonts?.ready) {
      documentWithFonts.fonts.ready.then(check).catch(check)
    } else {
      window.setTimeout(check, 100)
    }
  })

  MATERIAL_ICON_RENDERABLE_CACHE.set(iconName, promise)
  return promise
}

const RTL_CHAR_REGEX =
  /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/

const getTextDirection = (text?: string): 'rtl' | 'ltr' => {
  if (!text) return 'ltr'
  return RTL_CHAR_REGEX.test(text) ? 'rtl' : 'ltr'
}

const extractRubyText = (tokens?: RubyToken[]): string => {
  if (!tokens || tokens.length === 0) return ''
  return tokens.map(token => token?.text ?? '').join('')
}

const getLineTextDirection = (
  value: RealtimeDiagramLineText
): 'rtl' | 'ltr' => {
  if (value.ruby && value.ruby.length > 0) {
    return getTextDirection(extractRubyText(value.ruby))
  }
  return getTextDirection(value.text)
}

const hasRtlInMultiLineText = (value: RealtimeDiagramMultiLineText): boolean => {
  if (value.ruby && value.ruby.length > 0) {
    return value.ruby.some(lineTokens =>
      getTextDirection(extractRubyText(lineTokens)) === 'rtl'
    )
  }

  return value.text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .some(line => getTextDirection(line) === 'rtl')
}

const renderRubyTokens = (tokens: RubyToken[]) => {
  return tokens.map((token, index) => {
    if (!token || token.text == null) {
      return null
    }

    if (token.ruby && token.ruby.length > 0) {
      return (
        <ruby key={`${token.text}-${index}`}>
          {token.text}
          <rt>{token.ruby}</rt>
        </ruby>
      )
    }

    return <Fragment key={`${token.text}-${index}`}>{token.text}</Fragment>
  })
}

const renderLineTextWithRuby = (value: RealtimeDiagramLineText) => {
  if (!value.ruby || value.ruby.length === 0) {
    return value.text
  }

  return renderRubyTokens(value.ruby)
}

const renderDesc = (desc: RealtimeDiagramMultiLineText) => {
  if (desc.ruby && desc.ruby.length > 0) {
    return desc.ruby.map((lineTokens, index) => {
      const lineText = extractRubyText(lineTokens)
      return (
        <li key={`desc-ruby-${index}`} dir={getTextDirection(lineText)}>
          {renderRubyTokens(lineTokens)}
        </li>
      )
    })
  }

  return desc.text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map((line, index) => (
      <li key={`desc-${index}`} dir={getTextDirection(line)}>
        {line}
      </li>
    ))
}

const getDisplayNumber = (index: number): string => {
  return String(Math.min(index + 1, 999))
}

type RealtimeDiagramIconProps = {
  iconName?: string
  color: string
}

const RealtimeDiagramIcon: React.FC<RealtimeDiagramIconProps> = ({
  iconName,
  color,
}) => {
  const normalizedIconName = normalizeIconName(iconName)
  const [renderableIconName, setRenderableIconName] = useState<string | null>(
    null
  )

  useEffect(() => {
    let mounted = true

    setRenderableIconName(null)

    if (!normalizedIconName) {
      return () => {
        mounted = false
      }
    }

    isRenderableMaterialIcon(normalizedIconName).then(renderable => {
      if (mounted && renderable) {
        setRenderableIconName(normalizedIconName)
      }
    })

    return () => {
      mounted = false
    }
  }, [normalizedIconName])

  if (renderableIconName) {
    return (
      <Box
        component="span"
        className="material-symbols-outlined"
        aria-hidden="true"
        sx={{
          color,
          fontSize: '29px',
          lineHeight: 1,
          display: 'inline-block',
          fontVariationSettings:
            "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24",
        }}
      >
        {renderableIconName}
      </Box>
    )
  }

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        color,
        fontSize: '29px',
        lineHeight: 1,
        fontWeight: 'bold',
        display: 'inline-block',
      }}
    >
      〇
    </Box>
  )
}

export const RealtimeDiagramPreview: React.FC<RealtimeDiagramPreviewProps> = ({
  diagram,
  editMode = false,
  visualId,
  onDeleteRealtimeDiagram,
  scale = 1.4,
}) => {
  const [hovered, setHovered] = useState(false)
  const scaledWidth = useMemo(() => BASE_WIDTH * scale, [scale])

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: `${scaledWidth}px`,
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: `${BASE_WIDTH}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Noto Sans JP", sans-serif',
        }}
      >
        <Box
          sx={{
            width: '100%',
            border: '1px solid #bdbdbd',
            bgcolor: '#ffffff',
            color: '#333333',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Noto Sans JP", sans-serif',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              minHeight: '40px',
              bgcolor: '#e5e5e5',
              borderBottom: '1px solid #c9c9c9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              py: '6px',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            <Typography
              component="div"
              dir={getLineTextDirection(diagram.main_title)}
              sx={{
                width: '100%',
                fontSize: '15px',
                fontWeight: 'bold',
                textAlign: 'center',
                lineHeight: 1.15,
                wordBreak: 'break-word',
                '& rt': {
                  fontSize: '10px',
                  fontWeight: 'normal',
                  color: '#333333',
                },
              }}
            >
              {renderLineTextWithRuby(diagram.main_title)}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: '370px',
              overflowY: 'auto',
              overflowX: 'hidden',
              px: '10px',
              py: '12px',
              boxSizing: 'border-box',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              '& ruby': {
                rubyPosition: 'over',
              },
              '& rt': {
                fontSize: '8px',
                lineHeight: 1,
                fontWeight: 'normal',
                color: '#333333',
              },
            }}
          >
            {diagram.items.map((item, index) => {
              const color = ITEM_COLORS[index % ITEM_COLORS.length]
              const number = getDisplayNumber(index)
              const descHasRtl = hasRtlInMultiLineText(item.desc)

              return (
                <Box
                  key={`rt-diagram-item-${index}`}
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    pt: index === 0 ? 0 : '8px',
                    mt: index === 0 ? 0 : '10px',
                    borderTop: index === 0 ? 'none' : '1px solid #b8b8b8',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: '46px',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '4px',
                      boxSizing: 'border-box',
                      pt: '2px',
                    }}
                  >
                    <RealtimeDiagramIcon iconName={item.icon_name} color={color} />

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        bgcolor: color,
                        color: '#ffffff',
                        fontSize: number.length >= 3 ? '9px' : '10px',
                        fontWeight: 'bold',
                        lineHeight: 1,
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        textAlign: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {number}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      pr: '2px',
                    }}
                  >
                    <Typography
                      component="div"
                      dir={getLineTextDirection(item.title)}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        mb: '3px',
                        lineHeight: 1.25,
                        wordBreak: 'break-word',
                        textAlign: 'start',
                        '& rt': {
                          fontSize: '8px',
                        },
                      }}
                    >
                      {renderLineTextWithRuby(item.title)}
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        paddingInlineStart: '22px',
                        fontSize: '12px',
                        lineHeight: 1.35,
                        color: '#333333',
                        textAlign: 'start',
                        listStylePosition: descHasRtl ? 'inside' : 'outside',
                        '& li': {
                          my: '1px',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        },
                        '& rt': {
                          fontSize: '7px',
                        },
                      }}
                    >
                      {renderDesc(item.desc)}
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>

      {editMode && hovered && visualId && onDeleteRealtimeDiagram && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ConversationImageDelButton
            onClick={() => onDeleteRealtimeDiagram(visualId)}
          />
        </Box>
      )}
    </Box>
  )
}